import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useTextHighlight } from '../utils/highlightUtils';

const Class11NotePractice01 = () => {
  const navigate = useNavigate();

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // Format time function
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const formatAudioTime = (t) => {
    if (isNaN(t)) return '00:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />

      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>

          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>

            {/* Icons container with larger size and spacing */}
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 017.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>

              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Title Bar (fixed under top bar) */}
        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>
          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>
          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>

      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        {/* Two-Column Main Content Area */}
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4 text-center">Nutmeg – a valuable spice</h2>
              <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                The nutmeg tree, <i>Myristica fragrans</i>, is a large evergreen tree native to Southeast Asia.
              </h3>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  Until the late 18th century, it only grew in one place in the world: a small group of islands in the Banda Sea, part of the Moluccas – or Spice Islands – in northeastern Indonesia. The tree is thickly branched with dense foliage of tough, dark green oval leaves, and produces small, yellow, bell-shaped flowers and pale yellow pear-shaped fruits. The fruit is encased in a flesh husk. When the fruit is ripe, this husk splits into two halves along a ridge running the length of the fruit. Inside is a purple-brown shiny seed, 2-3 cm long by about 2 cm across, surrounded by a lacy red or crimson covering called an ‘aril’. These are the sources of the two spices nutmeg and mace, the former being produced from the dried seed and the latter from the aril.
                </p>
                <p>
                  Nutmeg was a highly prized and costly ingredient in European cuisine in the Middle Ages, and was used as a flavouring, medicinal, and preservative agent. Throughout this period, the Arabs were the exclusive importers of the spice to Europe. They sold nutmeg for high prices to merchants based in Venice, but they never revealed the exact location of the source of this extremely valuable commodity. The Arab-Venetian dominance of the trade finally ended in 1512, when the Portuguese reached the Banda Islands and began exploiting its precious resources.
                </p>
                <p>
                  Always in danger of competition from neighbouring Spain, the Portuguese began subcontracting their spice distribution to Dutch traders. Profits began to flow into the Netherlands, and the Dutch commercial fleet swiftly grew into one of the largest in the world. The Dutch quietly gained control of most of the shipping and trading of spices in Northern Europe. Then, in 1580, Portugal fell under Spanish rule, and by the end of the 16th century the Dutch found themselves locked out of the market. As prices for pepper, nutmeg, and other spices soared across Europe, they decided to fight back.
                </p>
                <p>
                  In 1602, Dutch merchants founded the VOC, a trading corporation better known as the Dutch East India Company. By 1617, the VOC was the richest commercial operation in the world. The company had 50,000 employees worldwide, with a private army of 30,000 men and a fleet of 200 ships. At the same time, thousands of people across Europe were dying of the plague, a highly contagious and deadly disease. Doctors were desperate for a way to stop the spread of this disease, and they decided nutmeg held the cure. Everybody wanted nutmeg, and many were willing to spare no expense to have it. Nutmeg bought for a few pennies in Indonesia could be sold for 68,000 times its original cost on the streets of London. The only problem was the short supply. And that’s where the Dutch found their opportunity.
                </p>
                <p>
                  The Banda Islands were ruled by local sultans who insisted on maintaining a neutral trading policy towards foreign powers. This allowed them to avoid the presence of Portuguese or Spanish troops on their soil, but it also left them unprotected from other invaders. In 1621, the Dutch arrived and took over. Once securely in control of the Bandas, the Dutch went to work protecting their new investment. They concentrated all nutmeg production into a few easily guarded areas, uprooting and destroying any trees outside the plantation zones. Anyone caught growing a nutmeg seedling or carrying seeds without the proper authority was severely punished. In addition, all exported nutmeg was covered with lime to make sure there was no chance a fertile seed which could be grown elsewhere would leave the islands. There was only one obstacle to Dutch domination. One of the Banda Islands, a sliver of land called Run, only 3 km long by less than 1 km wide, was under the control of the British. After decades of fighting for control of this tiny island, the Dutch and British arrived at a compromise settlement, the Treaty of Breda, in 1667. Intent on securing their hold over every nutmeg-producing island, the Dutch offered a trade: if the British would give them the island of Run, they would in turn give Britain a distant and much less valuable island in North America. The British agreed. That other island was Manhattan, which is how New Amsterdam became New York. The Dutch now had a monopoly over the nutmeg trade which would last for another century.
                </p>
                <p>
                  Then, in 1770, a Frenchman named Pierre Poivre successfully smuggled nutmeg plants to safety in Mauritius, an island off the coast of Africa. Some of these were later exported to the Caribbean where they thrived, especially on the island of Grenada. Next, in 1778, a volcanic eruption in the Banda region caused a tsunami that wiped out half the nutmeg groves. Finally, in 1809, the British returned to Indonesia and seized the Banda Islands by force. They returned the islands to the Dutch in 1817, but not before transplanting hundreds of nutmeg seedlings to plantations in several locations across southern Asia. The Dutch nutmeg monopoly was over.
                </p>
                <p>
                  Today, nutmeg is grown in Indonesia, the Caribbean, India, Malaysia, Papua New Guinea and Sri Lanka, and world nutmeg production is estimated to average between 10,000 and 12,000 tonnes per year.
                </p>
              </div>
            </div>
          </div>





          {/* Right: Questions */}
          <div
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
            style={{ overflowAnchor: 'none' }}
          >
            {/* Questions 1 – 4: Notes */}
            <div className="mb-8 w-full">
              <h3 className="text-[15px] font-bold mb-3 text-left">Questions 1 – 4</h3>
              <p className="text-[14px] mb-4 text-left">
                Complete the notes below.<br />
                Choose <strong>ONE WORD ONLY</strong> from the passage for each answer.
              </p>
              <ul className="list-disc list-inside space-y-2 text-[14px] font-medium text-black text-left">
                <li>
                  the leaves of the tree are&nbsp;
                  <input
                    ref={(el) => (questionRefs.current[0] = el)}
                    type="text"
                    placeholder="1"
                    className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[0] || ""}
                    onChange={(e) => {
                      const newAnswers = [...answers];
                      newAnswers[0] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                  &nbsp;in shape
                </li>
                <li>
                  the&nbsp;
                  <input
                    ref={(el) => (questionRefs.current[1] = el)}
                    type="text"
                    placeholder="2"
                    className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[1] || ""}
                    onChange={(e) => {
                      const newAnswers = [...answers];
                      newAnswers[1] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                  &nbsp;surrounds the fruit and breaks open when the fruit is ripe
                </li>
                <li>
                  the&nbsp;
                  <input
                    ref={(el) => (questionRefs.current[2] = el)}
                    type="text"
                    placeholder="3"
                    className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[2] || ""}
                    onChange={(e) => {
                      const newAnswers = [...answers];
                      newAnswers[2] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                  &nbsp;is used to produce the spice nutmeg
                </li>
                <li>
                  the covering known as the aril is used to produce&nbsp;
                  <input
                    ref={(el) => (questionRefs.current[3] = el)}
                    type="text"
                    placeholder="4"
                    className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow w-[140px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[3] || ""}
                    onChange={(e) => {
                      const newAnswers = [...answers];
                      newAnswers[3] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </li>
                <li>the tree has yellow flowers and fruit</li>
              </ul>
            </div>

            {/* Questions 8 – 13: Table */}
            <div className="w-full">
              <h3 className="text-[15px] font-bold mb-3 text-left">Questions 8 – 13</h3>
              <p className="text-[14px] mb-4 text-left">
                Complete the table below.<br />
                Choose <strong>ONE WORD ONLY</strong> from the passage for each answer.
              </p>

              <table className="w-full border border-gray-300 text-[14px] text-left">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-300">
                    <th className="p-2 border-r border-gray-300 w-[25%]">Period</th>
                    <th className="p-2 border-r border-gray-300 w-[55%]">Event</th>
                    <th className="p-2 border-gray-300 w-[20%]">Answer</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Row: Middle Ages */}
                  <tr className="border-t border-gray-300 align-top">
                    <td className="p-2 border-r border-gray-300 font-semibold">Middle Ages</td>
                    <td className="p-2 border-r border-gray-300">Nutmeg was brought to Europe by the</td>
                    <td className="p-2">
                      <input
                        ref={(el) => (questionRefs.current[7] = el)}
                        type="text"
                        placeholder="8"
                        className="inline-block w-full rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                        value={answers[7] || ""}
                        onChange={(e) => {
                          const newAnswers = [...answers];
                          newAnswers[7] = e.target.value;
                          setAnswers(newAnswers);
                        }}
                      />
                    </td>
                  </tr>

                  {/* Row: 16th century */}
                  <tr className="border-t border-gray-300 align-top">
                    <td className="p-2 border-r border-gray-300 font-semibold">16th century</td>
                    <td className="p-2 border-r border-gray-300" colSpan={2}>
                      European nations took control of the nutmeg trade
                    </td>
                  </tr>

                  {/* Row: 17th century */}
                  <tr className="border-t border-gray-300 align-top">
                    <td className="p-2 border-r border-gray-300 font-semibold">17th century</td>
                    <td className="p-2 border-r border-gray-300">Demand for nutmeg grew, as it was believed to be effective against the disease known as the</td>
                    <td className="p-2">
                      <input
                        ref={(el) => (questionRefs.current[8] = el)}
                        type="text"
                        placeholder="9"
                        className="inline-block w-full rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 shadow text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                        value={answers[8] || ""}
                        onChange={(e) => {
                          const newAnswers = [...answers];
                          newAnswers[8] = e.target.value;
                          setAnswers(newAnswers);
                        }}
                      />
                    </td>
                  </tr>

                  {/* Row: The Dutch */}
                  <tr className="border-t border-gray-300 align-top">
                    <td className="p-2 border-r border-gray-300 font-semibold">The Dutch</td>
                    <td className="p-2 border-r border-gray-300">
                      – took control of the Banda Islands<br />
                      – restricted nutmeg production to a few areas<br />
                      – put&nbsp;
                      <input
                        ref={(el) => (questionRefs.current[9] = el)}
                        type="text"
                        placeholder="10"
                        className="inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0.5 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                        value={answers[9] || ""}
                        onChange={(e) => {
                          const newAnswers = [...answers];
                          newAnswers[9] = e.target.value;
                          setAnswers(newAnswers);
                        }}
                      />
                      &nbsp;on nutmeg to avoid it being cultivated outside the islands<br />
                      – finally obtained the island of&nbsp;
                      <input
                        ref={(el) => (questionRefs.current[10] = el)}
                        type="text"
                        placeholder="11"
                        className="inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0.5 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                        value={answers[10] || ""}
                        onChange={(e) => {
                          const newAnswers = [...answers];
                          newAnswers[10] = e.target.value;
                          setAnswers(newAnswers);
                        }}
                      />
                      &nbsp;from the British
                    </td>
                    <td className="p-2"></td>
                  </tr>

                  {/* Row: Late 18th century */}
                  <tr className="border-t border-gray-300 align-top">
                    <td className="p-2 border-r border-gray-300 font-semibold">Late 18th century</td>
                    <td className="p-2 border-r border-gray-300">
                      1770 – nutmeg plants were secretly taken to
                      <input
                        ref={(el) => (questionRefs.current[11] = el)}
                        type="text"
                        placeholder="12"
                        className="inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0.5 shadow ml-1 w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                        value={answers[11] || ""}
                        onChange={(e) => {
                          const newAnswers = [...answers];
                          newAnswers[11] = e.target.value;
                          setAnswers(newAnswers);
                        }}
                      />
                    </td>
                    <td className="p-2"></td>
                  </tr>

                  {/* Row: Late 18th century (continued) */}
                  <tr className="border-t border-gray-300 align-top">
                    <td className="p-2 border-r border-gray-300 font-semibold"></td>
                    <td className="p-2 border-r border-gray-300">
                      1778 – half the Banda Islands’ nutmeg plantations were destroyed by a&nbsp;
                      <input
                        ref={(el) => (questionRefs.current[12] = el)}
                        type="text"
                        placeholder="13"
                        className="inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-0.5 shadow w-[110px] max-w-full text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                        value={answers[12] || ""}
                        onChange={(e) => {
                          const newAnswers = [...answers];
                          newAnswers[12] = e.target.value;
                          setAnswers(newAnswers);
                        }}
                      />
                    </td>
                    <td className="p-2"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>








        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-start px-4 py-3">
        {/* New Navigation Bar */}
        <div className="flex items-center max-w-3xl space-x-2 overflow-x-auto no-scrollbar">
          {/* Part Label with bar above */}
          <div className="flex flex-col items-center mr-4 flex-shrink-0">
            <div className="h-1 w-12 mb-1 rounded-full bg-gray-300" />
            <span className="text-[15px] font-bold text-gray-700">Part 1</span>
          </div>

          {/* Numbers with lines above */}
          {[...Array(4).keys()].map((i) => {
            const num = i + 1;
            const isActive = currentQuestion === num;
            const isAnswered = answers[num - 1] && answers[num - 1] !== '';
            return (
              <div key={num} className="flex flex-col items-center flex-shrink-0">
                <div
                  className="h-1 w-7 mb-1 rounded-full"
                  style={{ backgroundColor: isAnswered ? '#C9A842' : '#D1D5DB' }}
                ></div>
                <button
                  onClick={() => {
                    setCurrentQuestion(num);
                    setTimeout(() => {
                      const input = questionRefs.current[num - 1];
                      if (input) {
                        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        input.focus();
                      }
                    }, 50);
                  }}
                  className={`w-7 h-7 flex items-center justify-center text-[15px] font-bold
              ${isActive ? 'border-2 border-sky-400 bg-white text-sky-700 rounded-md' : 'text-gray-700'}
              transition-colors duration-150`}
                  style={{ boxShadow: isActive ? '0 0 0 0px #38bdf8 inset' : 'none' }}
                >
                  {num}
                </button>
              </div>
            );
          })}

          {[...Array(6).keys()].map((i) => {
            const num = i + 8; // 8 to 13
            const isActive = currentQuestion === num;
            const isAnswered = answers[num - 1] && answers[num - 1] !== '';
            return (
              <div key={num} className="flex flex-col items-center flex-shrink-0">
                <div
                  className="h-1 w-7 mb-1 rounded-full"
                  style={{ backgroundColor: isAnswered ? '#C9A842' : '#D1D5DB' }}
                ></div>
                <button
                  onClick={() => {
                    setCurrentQuestion(num);
                    setTimeout(() => {
                      const input = questionRefs.current[num - 1];
                      if (input) {
                        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        input.focus();
                      }
                    }, 50);
                  }}
                  className={`w-7 h-7 flex items-center justify-center text-[15px] font-bold
              ${isActive ? 'border-2 border-sky-400 bg-white text-sky-700 rounded-md' : 'text-gray-700'}
              transition-colors duration-150`}
                  style={{ boxShadow: isActive ? '0 0 0 0px #38bdf8 inset' : 'none' }}
                >
                  {num}
                </button>
              </div>
            );
          })}
        </div>
      </div>









    </div>
  );
};

export default Class11NotePractice01;
